import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, User, X, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Booking {
  id: string;
  student_name: string;
  student_phone?: string;
  booking_date: string;
  status: string;
  created_at: string;
  schedule: {
    id: string;
    day_of_week: number;
    time_slot: string;
    court_number: number;
    instructor?: {
      name: string;
    };
    level?: {
      name: string;
      color: string;
    };
  };
}

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export default function MyBookings() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');

  useEffect(() => {
    fetchMyBookings();

    // Suscribirse a cambios en tiempo real
    const subscription = supabase
      .channel('my_bookings_channel')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'class_bookings' },
        () => {
          fetchMyBookings();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchMyBookings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('class_bookings')
        .select(`
          *,
          schedule:class_schedules(
            id,
            day_of_week,
            time_slot,
            court_number,
            instructor:instructors(name),
            level:class_levels(name, color)
          )
        `)
        .eq('user_id', user.id)
        .order('booking_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookings(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setLoading(false);
    }
  };

  const handleCancelBooking = async (booking: Booking) => {
    try {
      // Verificar si se puede cancelar (2 horas antes)
      const bookingDateTime = new Date(`${booking.booking_date}T${booking.schedule.time_slot}`);
      const now = new Date();
      const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);

      if (bookingDateTime <= twoHoursFromNow) {
        alert('No se puede cancelar la clase. Debe hacerlo al menos 2 horas antes del horario programado.');
        return;
      }

      const confirmCancel = window.confirm('¿Estás seguro de que quieres cancelar esta clase?');
      if (!confirmCancel) return;

      const { error } = await supabase
        .from('class_bookings')
        .update({ 
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancellation_reason: 'Cancelado por el usuario'
        })
        .eq('id', booking.id);

      if (error) throw error;

      alert('Clase cancelada exitosamente');
      fetchMyBookings();
    } catch (error: any) {
      alert('Error al cancelar la clase: ' + error.message);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const canCancelBooking = (booking: Booking) => {
    if (booking.status !== 'confirmed') return false;
    
    const bookingDateTime = new Date(`${booking.booking_date}T${booking.schedule.time_slot}`);
    const now = new Date();
    const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    
    return bookingDateTime > twoHoursFromNow;
  };

  const getFilteredBookings = () => {
    const now = new Date();
    
    return bookings.filter(booking => {
      const bookingDate = new Date(booking.booking_date);
      
      switch (filter) {
        case 'upcoming':
          return bookingDate >= now && booking.status === 'confirmed';
        case 'past':
          return bookingDate < now || booking.status === 'cancelled';
        default:
          return true;
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const filteredBookings = getFilteredBookings();

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Mis Reservas</h2>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filter === 'upcoming'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Próximas
          </button>
          <button
            onClick={() => setFilter('past')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filter === 'past'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pasadas
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todas
          </button>
        </div>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">
            {filter === 'upcoming' ? 'No tienes clases próximas' : 
             filter === 'past' ? 'No tienes clases pasadas' : 
             'No tienes reservas'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map((booking) => (
            <div
              key={booking.id}
              className={`border rounded-lg p-4 ${
                booking.status === 'cancelled' 
                  ? 'border-red-200 bg-red-50' 
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">
                        {formatDate(booking.booking_date)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span>{formatTime(booking.schedule.time_slot)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span>Cancha {booking.schedule.court_number}</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>Alumno: {booking.student_name}</span>
                    </div>
                    
                    {booking.schedule.instructor && (
                      <span>Instructor: {booking.schedule.instructor.name}</span>
                    )}
                    
                    {booking.schedule.level && (
                      <span
                        className="px-2 py-1 text-xs rounded-full text-white"
                        style={{ backgroundColor: booking.schedule.level.color }}
                      >
                        {booking.schedule.level.name}
                      </span>
                    )}
                  </div>

                  {booking.status === 'cancelled' && (
                    <div className="flex items-center space-x-2 mt-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">Clase cancelada</span>
                    </div>
                  )}
                </div>

                {booking.status === 'confirmed' && canCancelBooking(booking) && (
                  <button
                    onClick={() => handleCancelBooking(booking)}
                    className="flex items-center space-x-1 px-3 py-1 text-red-600 border border-red-300 rounded hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                    <span className="text-sm">Cancelar</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}